import {
  createContext,
  createEffect,
  createRoot,
  createSignal,
  getOwner,
  onCleanup,
  type Owner,
  type ParentProps,
  runWithOwner,
  useContext,
  type JSX,
  startTransition,
  For,
} from "solid-js"
import { Dialog as Kobalte } from "@kobalte/core/dialog"
import { makeEventListener } from "@solid-primitives/event-listener"
import { createDialogStack, type DialogStackItem } from "./dialog-stack"

type DialogElement = () => JSX.Element

type Active = DialogStackItem & {
  node: JSX.Element
  owner: Owner
}

const Context = createContext<ReturnType<typeof init>>()

function init() {
  const dialogs = createDialogStack<Active>()
  onCleanup(dialogs.clear)

  createEffect(() => {
    if (!dialogs.active()) return

    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key !== "Escape" || event.defaultPrevented || !dialogs.active()) return
      dialogs.close()
      event.preventDefault()
      event.stopPropagation()
    }

    // Nested popups and controls consume Escape before the dialog fallback.
    makeEventListener(window, "keydown", onKeyDown)
  })

  const mount = (element: DialogElement, owner: Owner, onClose: (() => void) | undefined, layer: number) => {
    const id = Math.random().toString(36).slice(2)
    const zIndex = 50 + layer * 10
    let dispose: (() => void) | undefined
    const [closing, setClosing] = createSignal(false)

    // Stacked dialogs render as sibling portals, so only the top layer may own the focus trap.
    const node = runWithOwner(owner, () =>
      createRoot((d: () => void) => {
        dispose = d
        return (
          <Kobalte
            modal={dialogs.active()?.id === id}
            open={!closing()}
            onOpenChange={(open: boolean) => {
              if (open || dialogs.active()?.id !== id) return
              dialogs.close(id)
            }}
          >
            <Kobalte.Portal>
              <Kobalte.Overlay
                data-component="dialog-overlay"
                style={{ "z-index": String(zIndex) }}
                onClick={() => dialogs.close(id)}
              />
              <div
                data-dialog-layer={layer}
                style={{
                  position: "fixed",
                  inset: "0",
                  "z-index": String(zIndex),
                  display: "flex",
                  "align-items": "center",
                  "justify-content": "center",
                  "pointer-events": "none",
                }}
              >
                {element()}
              </div>
            </Kobalte.Portal>
          </Kobalte>
        )
      }),
    )

    if (!dispose) return

    dialogs.add({ id, node, dispose, owner, onClose, closing, setClosing })
  }

  const push = (element: DialogElement, owner: Owner, onClose?: () => void) => {
    dialogs.clearClosing()
    mount(element, owner, onClose, dialogs.stack().length)
  }

  const show = (element: DialogElement, owner: Owner, onClose?: () => void) => {
    dialogs.clear()
    mount(element, owner, onClose, 0)
  }

  return {
    stack: dialogs.stack,
    active: dialogs.active,
    close: dialogs.close,
    show,
    push,
  }
}

export function DialogProvider(props: ParentProps) {
  const ctx = init()
  return (
    <Context.Provider value={ctx}>
      {props.children}
      <div data-component="dialog-stack">
        <For each={ctx.stack()}>{(item) => item.node}</For>
      </div>
    </Context.Provider>
  )
}

export function useDialog() {
  const ctx = useContext(Context)
  const owner = getOwner()

  if (!owner) {
    throw new Error("useDialog must be used within a DialogProvider")
  }
  if (!ctx) {
    throw new Error("useDialog must be used within a DialogProvider")
  }

  return {
    get active() {
      return ctx.active()
    },
    show(element: DialogElement, onClose?: () => void) {
      const base = ctx.active()?.owner ?? owner
      return startTransition(() => ctx.show(element, base, onClose))
    },
    push(element: DialogElement, onClose?: () => void) {
      const base = ctx.active()?.owner ?? owner
      return startTransition(() => ctx.push(element, base, onClose))
    },
    close() {
      ctx.close()
    },
  }
}
